import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Ico from "../components/Icons";
import useReveal from "../hooks/useReveal";
import SERVICES from "../data/services";
import { WHATSAPP_NUMBER, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_HREF, CONTACT_EMAIL } from "../data/site";

const CONTACTS = [
  { ico:"phone", lbl:"Téléphone", val:CONTACT_PHONE_DISPLAY, href:CONTACT_PHONE_HREF },
  { ico:"mail",  lbl:"Email",     val:CONTACT_EMAIL, href:`mailto:${CONTACT_EMAIL}` },
  { ico:"pin",   lbl:"Adresse",   val:"Dakar, Sénégal",      href:"#" },
];

const HOURS = [
  ["Lundi – Vendredi", "8h00 – 18h00"],
  ["Samedi", "9h00 – 14h00"],
  ["Dimanche", "Fermé"],
];

const FAQ = [
  { q:"Sous combien de temps me répondez-vous ?", a:"Nous répondons à toutes les demandes envoyées via ce formulaire ou par email sous 24h ouvrées, souvent bien plus vite en journée." },
  { q:"Intervenez-vous en dehors de Dakar ?", a:"Oui, une grande partie de nos services (sites web, logiciels, marketing) se gère entièrement à distance. Pour les interventions physiques (dépannage, maintenance), nous couvrons Dakar et sa région, et étudions les demandes ponctuelles ailleurs au Sénégal." },
  { q:"Le devis est-il gratuit et sans engagement ?", a:"Toujours. Vous recevez un devis détaillé après notre premier échange, libre à vous de l'accepter, de le faire évoluer ou de ne pas donner suite." },
  { q:"Quels moyens de paiement acceptez-vous ?", a:"Nous acceptons les virements bancaires, Wave, Orange Money et les paiements échelonnés pour les projets de plus grande envergure, selon les modalités définies dans le devis." },
];

// Construction de la liste des sujets : demandes générales + services + packs + autre
const SUBJECT_OPTIONS = [
  { group:"Demande générale", value:"devis",  label:"Prise de contact pour un devis" },
  { group:"Demande générale", value:"projet", label:"Discussion d'un projet" },
  ...SERVICES.filter(s => !s.isPackRedirect).map(s => ({ group:"Services", value:s.slug, label:s.name })),
  { group:"Packs", value:"pack-starter",  label:"Pack Starter" },
  { group:"Packs", value:"pack-business", label:"Pack Business Pro" },
  { group:"Packs", value:"pack-premium",  label:"Pack Premium" },
];
const SUBJECT_GROUPS = ["Demande générale", "Services", "Packs"];

export default function Contact() {
  useReveal();
  const [searchParams] = useSearchParams();
  const [sent, setSent] = useState(false);

  const paramSujet = searchParams.get("sujet") || "";
  const isKnownSubject = SUBJECT_OPTIONS.some(o => o.value === paramSujet);

  const [subject, setSubject] = useState(isKnownSubject ? paramSujet : (paramSujet ? "autre" : ""));
  const [customSubject, setCustomSubject] = useState(isKnownSubject ? "" : paramSujet);
  const [message, setMessage] = useState("");
  const [highlightSubject, setHighlightSubject] = useState(false);
  const formRef = useRef(null);

  // Arrivée avec ?sujet=... : on scrolle jusqu'au formulaire et on anime le select
  useEffect(() => {
    if (!paramSujet) return;

    const t1 = setTimeout(() => {
      const element = formRef.current;

      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 140;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }

      setHighlightSubject(true);
    }, 80);

    const t2 = setTimeout(() => setHighlightSubject(false), 80 + 1700);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [paramSujet]);
  // Si l'utilisateur arrive/revient avec un nouveau paramètre ?sujet=..., on remet à jour la sélection
  useEffect(() => {
    if (!paramSujet) return;
    if (SUBJECT_OPTIONS.some(o => o.value === paramSujet)) {
      setSubject(paramSujet);
      setCustomSubject("");
    } else {
      setSubject("autre");
      setCustomSubject(paramSujet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramSujet]);

  const subjectLabel = useMemo(() => {
    if (subject === "autre") return customSubject.trim();
    return SUBJECT_OPTIONS.find(o => o.value === subject)?.label || "";
  }, [subject, customSubject]);

  const canSend = subjectLabel.length > 0 && message.trim().length > 0;

  function handleSubmit(e) {
    e.preventDefault();
    // À brancher sur votre service d'envoi (EmailJS, Formspree, backend maison...)
    setSent(true);
  }

  function handleWhatsapp() {
    if (!canSend) return;
    const text = `Sujet : ${subjectLabel}\n\nMessage : ${message.trim()}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="pagewrap">
      <section className="sec" style={{ background: "var(--w)" }}>
        <div className="secinr">
          <div className="sechdr r">
            <div className="lbl"><span className="lblline" />Discutons de votre projet</div>
            <h2 className="sh2">Contactez <em>Layetech</em></h2>
            <p className="slead">Une question, un devis, un projet en tête ? Notre équipe vous répond rapidement, par téléphone, email, WhatsApp ou via le formulaire ci-dessous. Le premier échange est toujours gratuit et sans engagement.</p>
          </div>

          <div className="contgrid r">
            {CONTACTS.map((c, i) => (
              <a className="contc" href={c.href} key={c.lbl} style={{ transitionDelay: `${i * .08}s` }}>
                <div className="contico"><Ico name={c.ico} size={21} color="#fff" /></div>
                <div className="contlbl">{c.lbl}</div>
                <div className="contval">{c.val}</div>
              </a>
            ))}
          </div>

          <div className="aboutgrid r" style={{ marginTop: "3rem", alignItems: "start", transitionDelay: ".15s" }}>
            <div className="contform" style={{ marginTop: 0 }} ref={formRef}>
              <form onSubmit={handleSubmit}>
                <div className="contgridform">
                  <div>
                    <label htmlFor="name">Nom complet</label>
                    <input id="name" name="name" type="text" placeholder="Votre nom" required />
                  </div>
                  <div>
                    <label htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" placeholder="vous@exemple.com" required />
                  </div>
                </div>

                <label htmlFor="subject">Sujet</label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className={highlightSubject ? "select-pulse" : undefined}
                >
                  <option value="" disabled>Choisissez un sujet...</option>
                  {SUBJECT_GROUPS.map(group => (
                    <optgroup label={group} key={group}>
                      {SUBJECT_OPTIONS.filter(o => o.group === group).map(o => (
                        <option value={o.value} key={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                  ))}
                  <option value="autre">Autre (précisez)</option>
                </select>

                {subject === "autre" && (
                  <>
                    <label htmlFor="customSubject">Précisez votre sujet</label>
                    <input
                      id="customSubject"
                      name="customSubject"
                      type="text"
                      placeholder="Votre sujet"
                      required
                      value={customSubject}
                      onChange={e => setCustomSubject(e.target.value)}
                    />
                  </>
                )}

                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  placeholder="Décrivez votre projet : votre activité, vos besoins, votre budget approximatif..."
                  required
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                />

                <div className="formbtns">
                  <button className="btnf" type="submit">
                    {sent ? "Message envoyé !" : "Envoyer le message"} <Ico name="arrowR" size={15} color="#fff" />
                  </button>
                  <button className="btnwa" type="button" disabled={!canSend} onClick={handleWhatsapp}>
                    <Ico name="whatsapp" size={17} color="#fff" /> Envoyer par WhatsApp
                  </button>
                </div>
              </form>
            </div>

            <div className="hourswrap">
              <h4>Horaires d'ouverture</h4>
              <div className="hourslist">
                {HOURS.map(([d, h]) => (
                  <div className="hoursrow" key={d}><span>{d}</span><span>{h}</span></div>
                ))}
              </div>
              <p style={{ fontSize: ".8rem", color: "var(--ink-60)", marginTop: "1.2rem", lineHeight: 1.7 }}>
                En dehors de ces horaires, laissez-nous un message : nous vous recontactons dès l'ouverture suivante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ PRATIQUE */}
      <section className="sec" style={{ background: "var(--off)" }}>
        <div className="secinr">
          <div className="sechdr r">
            <div className="lbl"><span className="lblline" />Avant de nous écrire</div>
            <h2 className="sh2">Questions <em>pratiques</em></h2>
          </div>
          <div className="faqlist r" style={{ transitionDelay: ".1s" }}>
            {FAQ.map(f => (
              <details className="faqitem" key={f.q}>
                <summary>{f.q}<span className="faqicon"><Ico name="arrowR" size={13} /></span></summary>
                <p className="faqa">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}